import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import Layout from '@/components/Layout';
import LoadingSpinner from '@/components/ui/LoadingSpinner';

// Auth pages load with the first paint for signed-out visitors, so they stay
// eager — a spinner on the login form is a worse first impression than a
// slightly larger entry chunk.
import LoginPage from '@/pages/auth/LoginPage';
import RegisterPage from '@/pages/auth/RegisterPage';
import LandingPage from '@/pages/LandingPage';

const ForgotPasswordPage = lazy(() => import('@/pages/auth/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('@/pages/auth/ResetPasswordPage'));

/**
 * Everything behind the login is split per route.
 *
 * All twenty pages used to be imported eagerly into one bundle, so every user
 * downloaded and parsed the 1,000-line Wearables screen and the AI Coach before
 * the dashboard could paint — on a phone that is the slowest part of the first
 * visit. Now a page's code arrives when the user opens it.
 */
const DashboardPage = lazy(() => import('@/pages/DashboardPage'));
const WorkoutsPage = lazy(() => import('@/pages/WorkoutsPage'));
const WorkoutDetailPage = lazy(() => import('@/pages/WorkoutDetailPage'));
const WorkoutGuidesPage = lazy(() => import('@/pages/WorkoutGuidesPage'));
const ExercisesPage = lazy(() => import('@/pages/ExercisesPage'));
const GoalsPage = lazy(() => import('@/pages/GoalsPage'));
const SocialPage = lazy(() => import('@/pages/SocialPage'));
const ProfilePage = lazy(() => import('@/pages/ProfilePage'));
const SettingsPage = lazy(() => import('@/pages/SettingsPage'));
const UserProfilePage = lazy(() => import('@/pages/UserProfilePage'));
const LockerRoomPage = lazy(() => import('@/pages/LockerRoomPage'));
const AICoachPage = lazy(() => import('@/pages/AICoachPage'));
const FitCoinPage = lazy(() => import('@/pages/FitCoinPage'));
const VibeSyncPage = lazy(() => import('@/pages/VibeSyncPage'));
const WearablesPage = lazy(() => import('@/pages/WearablesPage'));
const SchedulesPage = lazy(() => import('@/pages/SchedulesPage'));
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'));
const PrivacyPage = lazy(() => import('@/pages/legal/PrivacyPage'));
const TermsPage = lazy(() => import('@/pages/legal/TermsPage'));
const CreditsPage = lazy(() => import('@/pages/legal/CreditsPage'));

const FullPageSpinner: React.FC = () => (
  <div className="min-h-screen flex items-center justify-center">
    <LoadingSpinner size="lg" />
  </div>
);

const RouteFallback: React.FC = () => (
  <div className="flex items-center justify-center py-24">
    <LoadingSpinner size="lg" />
  </div>
);

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return <FullPageSpinner />;
  }

  if (!isAuthenticated) {
    // Carry the attempted URL through to the login page. Without it, anyone
    // opening a shared link to a workout — or returning to a bookmarked page
    // after their session lapsed — landed on the dashboard and had to find
    // their way back by hand.
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  return <>{children}</>;
};

const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <FullPageSpinner />;
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

const App: React.FC = () => {
  return (
    <div className="min-h-screen bg-app text-ink">
      <Suspense fallback={<FullPageSpinner />}>
        <Routes>
          {/* Landing Page */}
          <Route path="/" element={<LandingPage />} />

          {/* Public Routes */}
          <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
          <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
          <Route path="/forgot-password" element={<PublicRoute><ForgotPasswordPage /></PublicRoute>} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />

          {/* Legal pages — public, linked from Google's OAuth consent screen */}
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/credits" element={<CreditsPage />} />

          {/* Protected Routes */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            {/* An inner Suspense keeps the sidebar and header on screen while a
                page chunk loads, instead of replacing the whole app with a
                spinner on every navigation. */}
            <Route path="dashboard" element={<Suspense fallback={<RouteFallback />}><DashboardPage /></Suspense>} />
            <Route path="workouts" element={<Suspense fallback={<RouteFallback />}><WorkoutsPage /></Suspense>} />
            <Route path="workouts/:id" element={<Suspense fallback={<RouteFallback />}><WorkoutDetailPage /></Suspense>} />
            <Route path="workout-guides" element={<Suspense fallback={<RouteFallback />}><WorkoutGuidesPage /></Suspense>} />
            <Route path="exercises" element={<Suspense fallback={<RouteFallback />}><ExercisesPage /></Suspense>} />
            <Route path="goals" element={<Suspense fallback={<RouteFallback />}><GoalsPage /></Suspense>} />
            <Route path="schedules" element={<Suspense fallback={<RouteFallback />}><SchedulesPage /></Suspense>} />
            <Route path="social" element={<Suspense fallback={<RouteFallback />}><SocialPage /></Suspense>} />
            <Route path="locker-room" element={<Suspense fallback={<RouteFallback />}><LockerRoomPage /></Suspense>} />
            <Route path="ai-coach" element={<Suspense fallback={<RouteFallback />}><AICoachPage /></Suspense>} />
            <Route path="fitcoin" element={<Suspense fallback={<RouteFallback />}><FitCoinPage /></Suspense>} />
            <Route path="vibe-sync" element={<Suspense fallback={<RouteFallback />}><VibeSyncPage /></Suspense>} />
            <Route path="wearables" element={<Suspense fallback={<RouteFallback />}><WearablesPage /></Suspense>} />
            <Route path="profile" element={<Suspense fallback={<RouteFallback />}><ProfilePage /></Suspense>} />
            <Route path="settings" element={<Suspense fallback={<RouteFallback />}><SettingsPage /></Suspense>} />
            <Route path="users/:username" element={<Suspense fallback={<RouteFallback />}><UserProfilePage /></Suspense>} />

            {/* Unknown paths get a real 404 inside the app shell, so the user
                keeps their navigation and can see which URL failed. */}
            <Route path="*" element={<Suspense fallback={<RouteFallback />}><NotFoundPage /></Suspense>} />
          </Route>
        </Routes>
      </Suspense>
    </div>
  );
};

export default App;
